import { lazy, Suspense, useEffect, useRef, useState, type ComponentType } from "react";
import { ArrowRight, Database, Shield, Users, BarChart3 } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { HeroV2 } from "@/components/landing-v2/HeroV2";
import { MobileStickyCta } from "@/components/landing-v2/MobileStickyCta";
import { MobileCtaValueProps } from "@/components/landing-v2/MobileCtaValueProps";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/locale-link";
import { track } from "@/lib/analytics";
import { CapabilityCards } from "@/components/landing-v2/CapabilityCards";
import { ClosingConversion } from "@/components/landing-v2/ClosingConversion";
import { CONSUMER_JOIN_ROUTE, TRADE_JOIN_ROUTE } from "@/lib/homepage-ctas";

// Below-the-fold sections lazy-loaded to shrink the initial JS payload
// served for /home-v2 (Lighthouse "unused-javascript" mitigation).
const PlatformTrustNarrative = lazy(() =>
  import("@/components/PlatformTrustNarrative").then((m) => ({ default: m.PlatformTrustNarrative })),
);
const QuickLaunchRow = lazy(() =>
  import("@/components/landing-v2/QuickLaunchRow").then((m) => ({ default: m.QuickLaunchRow })),
);
const LiveRegistryFeed = lazy(() =>
  import("@/components/landing-v2/LiveRegistryFeed").then((m) => ({ default: m.LiveRegistryFeed })),
);
const AtlasPreview = lazy(() =>
  import("@/components/landing-v2/AtlasPreview").then((m) => ({ default: m.AtlasPreview })),
);
const AudienceRefined = lazy(() =>
  import("@/components/landing-v2/AudienceRefined").then((m) => ({ default: m.AudienceRefined })),
);
const NewsletterBand = lazy(() =>
  import("@/components/landing-v2/NewsletterBand").then((m) => ({ default: m.NewsletterBand })),
);
const LandingFaq = lazy(() =>
  import("@/components/landing-v2/LandingFaq").then((m) => ({ default: m.LandingFaq })),
);

const SectionFallback = ({ className = "min-h-[360px] md:min-h-[420px]" }: { className?: string }) => (
  <div aria-hidden className={className} />
);

function DeferredSection({
  component: Component,
  fallbackClassName,
  rootMargin = "600px",
}: {
  component: ComponentType;
  fallbackClassName: string;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      const timeout = window.setTimeout(() => setShouldRender(true), 1800);
      return () => window.clearTimeout(timeout);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin, shouldRender]);

  return (
    <div ref={ref}>
      {shouldRender ? (
        <Suspense fallback={<SectionFallback className={fallbackClassName} />}>
          <Component />
        </Suspense>
      ) : (
        <SectionFallback className={fallbackClassName} />
      )}
    </div>
  );
}

const consumerCtaImg =
  "https://vzzifwipsxxzghkauall.supabase.co/storage/v1/object/public/media-library/page/1776086529299-q3km0elpmgc.png";
const tradeCtaImg =
  "https://vzzifwipsxxzghkauall.supabase.co/storage/v1/object/public/media-library/page/1776086504543-hx0t5uyylm.png";
const ogImageUrl =
  "https://vzzifwipsxxzghkauall.supabase.co/storage/v1/object/public/media-library/system/1775477249335-gz0qs3a5k5h.png";

/**
 * Home v2 — promotion candidate (preview at /home-v2).
 * Brought to parity with production homepage prior to route swap.
 */
export default function HomeV2({ variant = "home" }: { variant?: "home" | "why-vitolier" } = {}) {
  const { t } = useTranslation();

  // Canonical public homepage for every viewport: signed-out mobile and desktop
  // visitors both render this component, which is responsive throughout.
  useEffect(() => {
    track("homepage_viewed", { variant: variant === "why-vitolier" ? "why_vitolier" : "home_v2" });
  }, [variant]);



  const trustPoints = [
    { icon: Database, label: t("landing.trustStructured") },
    { icon: Shield, label: t("landing.trustModerated") },
    { icon: Users, label: t("landing.trustCommunity") },
    { icon: BarChart3, label: t("landing.trustInsights") },
  ];

  const platformFoundation = {
    title: t("homeV2.foundationTitle"),
    subtitle: t("homeV2.foundationSubtitle"),
    descriptor: t("homeV2.foundationDescriptor"),
    bullets: [
      t("homeV2.foundationBullet1"),
      t("homeV2.foundationBullet2"),
      t("homeV2.foundationBullet3"),
    ],
  };

  const audienceLayers = [
    {
      title: t("homeV2.consumerTitle"),
      subtitle: t("homeV2.consumerSubtitle"),
      descriptor: t("homeV2.consumerDescriptor"),
      bullets: [
        t("homeV2.consumerBullet1"),
        t("homeV2.consumerBullet2"),
        t("homeV2.consumerBullet3"),
      ],
      cta: t("homeV2.consumerCta"),
      to: CONSUMER_JOIN_ROUTE,
      img: consumerCtaImg,
      alt: t("homeV2.consumerImgAlt"),
    },
    {
      title: t("homeV2.tradeTitle"),
      subtitle: t("homeV2.tradeSubtitle"),
      descriptor: t("homeV2.tradeDescriptor"),
      bullets: [
        t("homeV2.tradeBullet1"),
        t("homeV2.tradeBullet2"),
        t("homeV2.tradeBullet3"),
      ],
      cta: t("homeV2.tradeCta"),
      to: TRADE_JOIN_ROUTE,
      img: tradeCtaImg,
      alt: t("homeV2.tradeImgAlt"),
    },
  ];

  return (
    <div>
      <SeoHead
        title={t("homeV2.seoTitle")}
        description={t("homeV2.seoDescription")}
        ogImage={ogImageUrl}
        includeSiteJsonLd
      />

      <HeroV2 />

      {/* ─── TRUST BAR ─── */}
      <section className="py-6 md:py-12 bg-card/30 border-y border-border/40">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {trustPoints.map((point) => (
              <div key={point.label} className="flex items-center gap-2 md:gap-3 justify-center">
                <point.icon className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="text-sm text-muted-foreground font-medium">{point.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CIGAR JOURNEY / CIGAR JOURNAL CAPABILITIES ─── */}
      <CapabilityCards />

      <DeferredSection component={PlatformTrustNarrative} fallbackClassName="min-h-[380px] md:min-h-[360px]" />

      <DeferredSection component={QuickLaunchRow} fallbackClassName="min-h-[200px] md:min-h-[300px]" />

      <DeferredSection component={LiveRegistryFeed} fallbackClassName="min-h-[520px] md:min-h-[700px]" />

      {/* ─── THE REGISTRY foundation + audience split ─── */}
      <section className="editorial-section bg-[#2a1f1a]">
        <div className="container">
          <div className="mb-6 md:mb-14">
            <span className="editorial-subheading text-primary">{t("homeV2.registryEyebrow")}</span>
            <h2 className="editorial-heading mt-2 text-white">{t("homeV2.registryHeading")}</h2>
            <p className="text-sm text-white/75 mt-3 max-w-3xl leading-relaxed">
              {t("homeV2.registryIntro")}
            </p>
          </div>

          {/* Foundation card */}
          <div className="border-t border-white/15 pt-5 pb-5 bg-white/[0.04] rounded-xl px-4 md:px-7 md:pt-7 md:pb-7">
            <h3 className="font-display text-lg md:text-2xl font-semibold text-white">
              {platformFoundation.title}
            </h3>
            <p className="text-sm text-white/70 mt-2 leading-relaxed max-w-3xl">
              {platformFoundation.subtitle}
            </p>
            <p className="text-xs text-white/50 italic mt-3">{platformFoundation.descriptor}</p>
            <ul className="mt-3 space-y-1.5">
              {platformFoundation.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-white/60">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-primary/70 flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex justify-center">
              <p className="bg-white/[0.07] text-white/60 text-xs md:text-sm font-medium text-center rounded-full px-5 md:px-6 py-3">
                {t("homeV2.foundationCaption")}
              </p>
            </div>
          </div>

          {/* Audience layers — image left, text right */}
          <div className="space-y-4 md:space-y-6 mt-6 md:mt-8">
            {audienceLayers.map((col) => (
              <div
                key={col.title}
                className="grid grid-cols-1 md:grid-cols-[minmax(200px,1fr)_2fr] gap-4 md:gap-8 items-center border-t border-white/15 pt-4 md:pt-5"
              >
                <div className="flex items-center justify-center">
                  <img
                    src={col.img}
                    alt={col.alt}
                    className="w-full max-w-[200px] md:max-w-none rounded-lg object-contain h-[160px] md:h-[280px]"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h3 className="font-display text-lg md:text-2xl font-semibold text-white">
                    {col.title}
                  </h3>
                  <p className="text-sm text-white/70 mt-1.5 leading-relaxed">{col.subtitle}</p>
                  <p className="text-xs text-white/50 italic mt-2">{col.descriptor}</p>
                  <ul className="mt-2 space-y-1">
                    {col.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-white/60">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-primary/70 flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3">
                    <Link
                      to={col.to}
                      onClick={() =>
                        track("audience_cta_clicked", {
                          audience: col.to === TRADE_JOIN_ROUTE ? "trade" : "consumer",
                          destination: col.to,
                          surface: "home_v2_registry_split",
                        })
                      }
                    >
                      <Button size="lg" className="gap-2 min-h-[44px] font-medium rounded-lg">
                        {col.cta} <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DeferredSection component={AtlasPreview} fallbackClassName="min-h-[480px] md:min-h-[680px]" />

      <MobileCtaValueProps />

      <div className="hidden md:block">
        <DeferredSection component={AudienceRefined} fallbackClassName="min-h-[560px] md:min-h-[720px]" />
      </div>

      <ClosingConversion />

      <DeferredSection component={NewsletterBand} fallbackClassName="min-h-[300px] md:min-h-[380px]" />

      <DeferredSection component={LandingFaq} fallbackClassName="min-h-[280px] md:min-h-[420px]" />

      <MobileStickyCta />
    </div>
  );
}
